/* Twilio Voice browser softphone — pure PSTN telephony via @twilio/voice-sdk.
   Calls placed/received here are routed through Twilio (paid per minute). */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Device } from '@twilio/voice-sdk';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
    Phone, PhoneSlash, Microphone, MicrophoneSlash, Pause, Play,
    Hash, CheckCircle, WarningCircle, SpinnerGap, UserCircle, ArrowsClockwise,
} from '@phosphor-icons/react';

const KEYS = [
    ['1', ''], ['2', 'ABC'], ['3', 'DEF'],
    ['4', 'GHI'], ['5', 'JKL'], ['6', 'MNO'],
    ['7', 'PQRS'], ['8', 'TUV'], ['9', 'WXYZ'],
    ['*', ''], ['0', '+'], ['#', ''],
];

const DialKey = ({ d, sub, onClick, tid }) => (
    <button data-testid={tid} onClick={() => onClick(d)}
            className="aspect-square flex flex-col items-center justify-center rounded-2xl
                       bg-[var(--bg-surface)] hover:bg-[var(--bg-surface-hover)]
                       border border-[var(--border-default)] transition-all active:scale-95 select-none">
        <span className="text-3xl font-light">{d}</span>
        {sub && <span className="text-[10px] tracking-[0.25em] uppercase mt-0.5"
                      style={{ color: 'var(--text-secondary)' }}>{sub}</span>}
    </button>
);

const PHASE_LABEL = {
    idle: 'Připraveno',
    init: 'Inicializuji…',
    register: 'Registruji…',
    ready: 'Registrováno',
    failed: 'Chyba',
    incoming: 'Příchozí hovor',
    dialing: 'Vyzvání…',
    in_call: 'Hovor',
};

const TwilioPhone = () => {
    const [phase, setPhase] = useState('idle');
    const [identity, setIdentity] = useState(null);
    const [number, setNumber] = useState('');
    const [active, setActive] = useState(null);  // { from, direction, call }
    const [muted, setMuted] = useState(false);
    const [duration, setDuration] = useState(0);
    const [error, setError] = useState(null);

    const deviceRef = useRef(null);
    const callRef = useRef(null);
    const durTimer = useRef(null);
    const tokenRefreshTimer = useRef(null);

    const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

    const cleanupCall = useCallback(() => {
        if (durTimer.current) clearInterval(durTimer.current);
        durTimer.current = null;
        callRef.current = null;
        setActive(null);
        setDuration(0);
        setMuted(false);
        if (deviceRef.current) setPhase('ready');
        else setPhase('idle');
    }, []);

    const attachCallHandlers = useCallback((call, direction = 'outbound') => {
        callRef.current = call;
        setActive({ direction, from: call.parameters?.From || number, call });

        call.on('accept', () => {
            setPhase('in_call');
            setDuration(0);
            durTimer.current = setInterval(() => setDuration((d) => d + 1), 1000);
        });
        call.on('disconnect', cleanupCall);
        call.on('cancel', cleanupCall);
        call.on('reject', cleanupCall);
        call.on('error', (e) => {
            toast.error('Chyba hovoru: ' + (e.message || e.code));
            cleanupCall();
        });
    }, [number, cleanupCall]);

    const initDevice = useCallback(async () => {
        setPhase('init');
        setError(null);
        try {
            const r = await api.get('/twilio/token');
            const { token, identity: ident } = r.data;
            setIdentity(ident);
            // Cleanup any previous device
            if (deviceRef.current) {
                try { deviceRef.current.destroy(); } catch (_) {}
            }
            const device = new Device(token, {
                logLevel: 'warn',
                edge: 'frankfurt',  // EU edge for lower latency
                codecPreferences: ['opus', 'pcmu'],
            });
            deviceRef.current = device;

            device.on('registered', () => setPhase('ready'));
            device.on('unregistered', () => setPhase('idle'));
            device.on('error', (e) => {
                console.error('Twilio device error:', e);
                setError(e.message || String(e));
                setPhase('failed');
            });
            device.on('incoming', (call) => {
                setPhase('incoming');
                attachCallHandlers(call, 'inbound');
                toast.info(`Příchozí hovor: ${call.parameters?.From || 'neznámý'}`);
            });
            device.on('tokenWillExpire', async () => {
                try {
                    const rr = await api.get('/twilio/token');
                    await device.updateToken(rr.data.token);
                } catch (_) {}
            });

            setPhase('register');
            await device.register();
            // refresh token every 50 minutes
            if (tokenRefreshTimer.current) clearInterval(tokenRefreshTimer.current);
            tokenRefreshTimer.current = setInterval(async () => {
                try {
                    const rr = await api.get('/twilio/token');
                    await device.updateToken(rr.data.token);
                } catch (_) {}
            }, 50 * 60 * 1000);
        } catch (e) {
            const detail = e.response?.data?.detail || e.message;
            setError(detail);
            setPhase('failed');
            if (e.response?.status === 412) {
                toast.error('Twilio není nakonfigurován — v /twilio doplňte API Key + Secret + TwiML App SID');
            } else {
                toast.error('Init selhal: ' + detail);
            }
        }
    }, [attachCallHandlers]);

    useEffect(() => () => {
        try { deviceRef.current?.destroy(); } catch (_) {}
        if (tokenRefreshTimer.current) clearInterval(tokenRefreshTimer.current);
    }, []);

    const placeCall = useCallback(async () => {
        const target = number.trim();
        if (!target) return toast.error('Zadejte číslo');
        if (!deviceRef.current) return toast.error('Nejdřív se připojte');
        try {
            setPhase('dialing');
            const call = await deviceRef.current.connect({ params: { To: target } });
            attachCallHandlers(call, 'outbound');
        } catch (e) {
            toast.error('Volání selhalo: ' + (e.message || e.code));
            cleanupCall();
        }
    }, [number, attachCallHandlers, cleanupCall]);

    const acceptCall = () => callRef.current?.accept();
    const rejectCall = () => callRef.current?.reject();
    const hangup = () => callRef.current?.disconnect();
    const toggleMute = () => {
        const c = callRef.current;
        if (!c) return;
        c.mute(!muted);
        setMuted(!muted);
    };

    const sendDtmf = (d) => {
        if (phase === 'in_call' && callRef.current) {
            callRef.current.sendDigits(d);
        } else {
            setNumber((v) => v + d);
        }
    };

    const phaseColor =
        phase === 'ready' ? 'var(--success, #22c55e)' :
        phase === 'in_call' ? 'var(--brand)' :
        phase === 'incoming' || phase === 'dialing' ? '#f59e0b' :
        phase === 'failed' ? 'var(--danger, #ef4444)' : 'var(--text-secondary)';

    return (
        <div className="max-w-md mx-auto" data-testid="twilio-phone">
            <div className="mb-6">
                <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
                    <Phone size={28} style={{ color: 'var(--brand)' }} />
                    Twilio Telefon
                </h1>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    PSTN volání přes Twilio Programmable Voice
                </p>
            </div>

            <div className="rounded-2xl border p-5 mb-4"
                 style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <UserCircle size={20} style={{ color: 'var(--brand)' }} />
                        <span className="font-mono text-xs" data-testid="tp-identity">
                            {identity || '—'}
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider"
                         data-testid="tp-phase" style={{ color: phaseColor }}>
                        {phase === 'ready' || phase === 'in_call'
                            ? <CheckCircle size={14} weight="fill" />
                            : phase === 'failed'
                            ? <WarningCircle size={14} weight="fill" />
                            : <SpinnerGap size={14} className={
                                ['init', 'register', 'dialing'].includes(phase) ? 'animate-spin' : ''
                              } />}
                        {PHASE_LABEL[phase] || phase}
                    </div>
                </div>

                {phase === 'idle' || phase === 'failed' ? (
                    <button data-testid="tp-connect-btn" onClick={initDevice}
                            className="mt-3 w-full py-2.5 rounded-lg font-bold text-sm uppercase tracking-wider"
                            style={{ background: 'var(--brand)', color: '#000' }}>
                        Připojit k Twilio
                    </button>
                ) : (phase === 'ready' || phase === 'register' || phase === 'init') ? (
                    <button data-testid="tp-disconnect-btn"
                            onClick={() => { try { deviceRef.current?.destroy(); } catch (_) {} ; setPhase('idle'); }}
                            className="mt-3 w-full py-2 rounded-lg text-xs uppercase tracking-wider
                                       border hover:border-[var(--danger)] hover:text-[var(--danger)]"
                            style={{ borderColor: 'var(--border-default)' }}>
                        <ArrowsClockwise size={12} className="inline mr-1" /> Odpojit
                    </button>
                ) : null}

                {error && phase === 'failed' && (
                    <div className="mt-3 text-xs p-2 rounded"
                         style={{ background: 'rgba(239,68,68,0.1)', color: '#fca5a5' }}>
                        {error}
                    </div>
                )}
            </div>

            {(phase === 'in_call' || phase === 'incoming' || phase === 'dialing') && (
                <div className="rounded-2xl border p-6 mb-4 text-center"
                     data-testid="tp-active-card"
                     style={{ background: 'var(--bg-surface)', borderColor: phaseColor }}>
                    <div className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: phaseColor }}>
                        {PHASE_LABEL[phase]}
                    </div>
                    <div className="text-2xl font-mono font-bold mb-1" data-testid="tp-remote">
                        {active?.from || number}
                    </div>
                    {phase === 'in_call' && (
                        <div className="text-sm font-mono" style={{ color: 'var(--brand)' }}
                             data-testid="tp-duration">
                            {fmt(duration)}
                        </div>
                    )}
                </div>
            )}

            <div className="rounded-2xl border p-6"
                 style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                <input
                    data-testid="tp-number-input"
                    value={number}
                    onChange={(e) => setNumber(e.target.value)}
                    placeholder="+420…"
                    className="w-full mb-4 bg-transparent border-0 border-b py-2 text-2xl
                               font-mono text-center focus:outline-none focus:border-[var(--brand)]"
                    style={{ borderColor: 'var(--border-default)' }}
                />
                <div className="grid grid-cols-3 gap-2 mb-4">
                    {KEYS.map(([d, s]) => (
                        <DialKey key={d} d={d} sub={s} onClick={sendDtmf}
                                 tid={`tp-key-${d === '*' ? 'star' : d === '#' ? 'hash' : d}`} />
                    ))}
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <button data-testid="tp-mute-btn"
                            onClick={toggleMute}
                            disabled={phase !== 'in_call'}
                            className="py-3 rounded-xl border disabled:opacity-30 flex items-center justify-center"
                            style={{ borderColor: muted ? 'var(--danger)' : 'var(--border-default)' }}>
                        {muted ? <MicrophoneSlash size={20} color="var(--danger)" /> : <Microphone size={20} />}
                    </button>

                    {phase === 'incoming' ? (
                        <button data-testid="tp-accept-btn" onClick={acceptCall}
                                className="py-3 rounded-xl text-white font-bold flex items-center justify-center"
                                style={{ background: 'var(--success, #22c55e)' }}>
                            <Phone size={22} weight="fill" />
                        </button>
                    ) : (phase === 'in_call' || phase === 'dialing') ? (
                        <button data-testid="tp-hangup-btn" onClick={hangup}
                                className="py-3 rounded-xl text-white font-bold flex items-center justify-center"
                                style={{ background: 'var(--danger, #ef4444)' }}>
                            <PhoneSlash size={22} weight="fill" />
                        </button>
                    ) : (
                        <button data-testid="tp-call-btn" onClick={placeCall}
                                disabled={phase !== 'ready' || !number}
                                className="py-3 rounded-xl text-black font-bold disabled:opacity-30 flex items-center justify-center"
                                style={{ background: 'var(--brand)' }}>
                            <Phone size={22} weight="fill" />
                        </button>
                    )}

                    {phase === 'incoming' ? (
                        <button data-testid="tp-reject-btn" onClick={rejectCall}
                                className="py-3 rounded-xl text-white font-bold flex items-center justify-center"
                                style={{ background: 'var(--danger, #ef4444)' }}>
                            <PhoneSlash size={22} weight="fill" />
                        </button>
                    ) : (
                        <button onClick={() => setNumber('')}
                                className="py-3 rounded-xl border flex items-center justify-center"
                                style={{ borderColor: 'var(--border-default)' }}>
                            ⌫
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TwilioPhone;
